import { DataQueryField } from './data-query-field.model';
import { DataQueryOperator } from './data-query-operator.model';
import { Lookup } from '../../../shared-components/lookup/lookup.model';
import { DeleteRowDialogComponent } from '../delete-row-dialog/delete-row-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TylerMatTableDataSource } from '../../../shared-components/angular-material/extended.class';

export class DataQueryFieldFilter extends DataQueryField {

    // Static Properties
    static courseFilterDisplayName = '(Students Actively Enrolled Today in) Class';
    static courseFilterFieldName = 'StudentsActivelyEnrolledTodayInClass';
    static studentFilterFieldName = 'UserDefinedStudentFilter';
    static enrollmentFilterFieldName = 'StudentsEnrolledAsOf';
    static enrollmentFilterDisplayName = '(Include only students enrolled as of) Date';
    static studentListFilterFieldName = 'StudentsListfilter';
    static studentListFilterDisplayName = 'Students in Student List';
    static studentTeamFilterFieldName = 'StudentsTeamfilter';
    static studentTeamFilterDisplayName = 'Students in Student Team';
    static displayOperators = [
        '=',
        'Not=',
        '>',
        '>=',
        '<',
        '<=',
        'Between',
        'Is Blank',
        'Is Not Blank',
        'Contains',
        'In List',
        'Not In List',
        'Starts With'
    ];

    // Server Properties
    ApplyToAll = false;
    DisplayMenu = false;
    FilterID: number;
    FilterOptions: Lookup[] = [];
    Group = 'A';
    IsStudentFilter = false;
    IsCourseFilter = false;
    Operator: string;
    UseCurrentDate1 = false;
    UseCurrentDate2 = false;
    UseFilterForStudentSearch = true;
    UseFilterForCourseSearch = false;
    UseFilterForStaffSearch = false;
    Value: boolean | number | string = '';
    Value2: boolean | number | string = '';
    numericType: boolean;

    // Local Properties
    private _field: DataQueryField;
    private _dateValue: Date;
    private _dateValue2: Date;
    showFilterError1: boolean;
    showFilterError2: boolean;
    disableEdit: boolean;
    hidden: boolean;

    // Class Functions
    get studentFilter(): boolean {
        return this.IsStudentFilter || (this.field && this.field.FieldName == DataQueryFieldFilter.courseFilterFieldName);
    }

    get courseFilter(): boolean {
        return this.IsCourseFilter;
    }

    get field(): DataQueryField {
        return this._field;
    }

    set field(value: DataQueryField) {
        this._field = value;
        if (this._field && this._field.DataType) {

            // Making sure this filter is a part of the field
            if (this._field.Filters) {
                const indexOfThis = this._field.Filters.indexOf(<DataQueryFieldFilter>this);
                if (indexOfThis < 0) {
                    this._field.Filters.push(this);
                }
            } else {
                this._field.Filters = [];
                this._field.Filters.push(this);
            }

            // Determining if the filter is a numeric type
            this.numericType = this._field.DataType.toLowerCase() == 'int'
                || this._field.DataType.toLowerCase() == 'decimal'
                || this._field.DataType.toLowerCase() == 'float'
                || this._field.DataType.toLowerCase() == 'money'
                || this._field.DataType.toLowerCase() == 'bigint'
                || this._field.DataType.toLowerCase() == 'smallint'
                || this._field.DataType.toLowerCase() == 'tinyint'
                || this._field.DataType.toLowerCase() == 'numeric';

            // Setting the initial operator if none is present or it is invalid
            if (!this.Operator || !this.getFilterOperators().includes(this.Operator)) {
                this.Operator = this.getFilterOperators()[0];
            }
        }
    }

    get dateValue(): Date {
        return this._dateValue;
    }

    set dateValue(value: Date) {
        this._dateValue = value;
        this.setValueFromDate(this._dateValue, 1);
    }

    get dateValue2(): Date {
        return this._dateValue2;
    }

    set dateValue2(value: Date) {
        this._dateValue2 = value;
        this.setValueFromDate(this._dateValue2, 2);
    }

    constructor(field?: DataQueryField) {
        // POST: sets the operator to the first viable operator
        super();

        // If a field is provided, add the field to the filter and the filter to the field
        if (field) {
            this.field = field;
        }
    }

    // Methods
    duplicate(field?: DataQueryField): DataQueryFieldFilter {
        const newFilter = new DataQueryFieldFilter();
        Object.assign(newFilter, this);
        newFilter.field = field;
        return newFilter;
    }

    getFilterOperators(): string[] {
        // PRE: a field to get the operators for
        // POST: returns the operators for a field's filters
        switch (this.field.FilterType) {
            case 0:
                if (this.field.DataType.toLowerCase() == 'bit') {
                    return DataQueryFieldFilter.displayOperators.filter(operator =>
                        operator != 'Between'
                        && operator != 'Contains'
                        && operator != 'In List'
                        && operator != 'Not In List'
                        && operator != 'Starts With'
                    );
                } else {
                    return DataQueryFieldFilter.displayOperators;
                }
            case 1:
                return DataQueryFieldFilter.displayOperators.filter(operator =>
                    operator != 'Contains'
                    && operator != 'In List'
                    && operator != 'Not In List'
                    && operator != 'Starts With'
                );
            case 3:
                return DataQueryFieldFilter.displayOperators.filter(operator =>
                    operator != 'Between'
                    && operator != 'In List'
                    && operator != 'Not In List'
                );
            default:
                return DataQueryFieldFilter.displayOperators;
        }
    }

    setValueFromDate(date: Date, value = 1) {
        // PRE: a date to extract a value from and the value we want to set (either 1 or 2)
        // POST: sets the value based on the date
        if (date == null) {
            if (value == 1) {
                this.Value = '';
            } else {
                this.Value2 = '';
            }
        } else {
            if (value == 1) {
                this.Value = (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
            } else {
                this.Value2 = (date.getMonth() + 1) + '/' + date.getDate() + '/' + date.getFullYear();
            }
        }
    }

    useCodeDescription(V10: boolean) {
        // PRE: whether or not this is V10. Also, must have a field.
        // POST: determines whether this should use the code or the code description
        if (!V10) {
            return this.field.FieldName == 'Lives With'
                || this.field.FieldName == 'Student Suffix'
                || this.field.FieldName == 'Graduation Plan'
                || this.field.FieldName == 'Subject Type'
                || this.field.FieldName == 'Language of Instruction'
                || this.field.FieldName == 'District Department'
                || this.field.FieldName == 'Graduation Department'
                || this.field.FieldName == 'Additional Graduation Department'
                || this.field.FieldName == 'Subject Term Type'
                || this.field.FieldName == 'Credit Delivery Term Type'
                || this.field.FieldName == 'Pri Enrl Site Name'
                || this.field.FieldName == 'EnrollmentStatus';
        } else {
            return this.field.FieldName != 'Grade';
        }
    }

    disabled() {
        // POST: returns true if this should be disabled
        return this.Operator == 'Is Blank' || this.Operator == 'Is Not Blank';
    }

    delete(
        dialog: MatDialog,
        filterArray: DataQueryFieldFilter[] = [],
        filterFields: DataQueryField[] = [],
        forceDelete = false,
        tableData?: TylerMatTableDataSource,
        callback: Function = null
    ) {
        // PRE: a filter that is in the filter data
        // 		The filters field must not be null and the field must have a filters array that the filter is a part of
        // POST: deletes the filter from the filter data

        // Creating helper function to update table data
        const updateTableData = () => {
            if (tableData) {
                tableData.data = filterArray;
            }
        };

        // Checking that we have a field
        if (this.field && this.field.Filters) {

            // If this is the only filter for that field, we need to perform additional checks
            if (this.field.Filters.length == 1) {

                // Creating a helping function
                const removeFilter = () => {
                    if (this.field) {
                        this.field.Filters.splice(this.field.Filters.indexOf(this), 1);
                    }
                    if (filterFields) {
                        filterFields.splice(filterFields.indexOf(this.field), 1);
                    }
                    filterArray.splice(filterArray.indexOf(this), 1);
                };

                // If we are forcing deletion, just go ahead and remove it
                if (forceDelete) {
                    removeFilter();
                    updateTableData();
                }

                // If we're not forcing deletion, prompt the user
                else {
                    const dialogBox = dialog.open(DeleteRowDialogComponent, {
                        disableClose: true,
                        data: {
                            message: 'This will delete ' + this.field.DisplayName + ' from the filter.'
                        }
                    });
                    const subscription = dialogBox.afterClosed().subscribe(result => {
                        if (result) {
                            removeFilter();
                            updateTableData();
                            if (callback) {
                                callback();
                            }
                        }
                        subscription.unsubscribe();
                    });
                }
            }

            // If there are multiple filters for the field, remove it from the field and this component
            else {
                this.field.Filters.splice(this.field.Filters.indexOf(this), 1);
                filterArray.splice(filterArray.indexOf(this), 1);
                updateTableData();
            }
        }

        // If there isn't a field, then we just remove it from this component
        else {
            filterArray.splice(filterArray.indexOf(this), 1);
            updateTableData();
        }
    }
}
